import { useImperativeHandle, useState } from 'react';
import * as v from 'valibot';
import { useForm, useStore } from '@tanstack/react-form';

import { Button, GroupField, Input, InputField, SelectField } from '@nvision/ui';

import { useCountries, useStates } from '../../../hooks';
import { type LocationSearchFormValues, locationSearchFormSchema, type State } from '../../../models';
import { PortSearchModal } from '../../port';

import styles from './location-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface LocationSearchFormProps {
  defaultValues?: LocationSearchFormValues;
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: LocationSearchFormValues) => void;
  handleReset: () => void;
}

const getDefaultSearchFormValues = (): LocationSearchFormValues =>
  v.getDefaults(locationSearchFormSchema) as unknown as LocationSearchFormValues;

export const LocationSearchForm: React.FC<LocationSearchFormProps> = ({
  defaultValues,
  formRef,
  handleSubmit,
  handleReset,
}) => {
  const [isPortSearchOpen, setIsPortSearchOpen] = useState(false);

  const form = useForm({
    defaultValues: defaultValues || getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: locationSearchFormSchema,
      onSubmit: locationSearchFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      handleReset();
    },
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const { data: countries, isLoading: countriesLoading } = useCountries();
  const countryCode = useStore(form.store, (store) => store.values.countryCode);
  const { data: states, isLoading: statesLoading } = useStates({
    CountryCode: String(countryCode?.id) ?? '',
  });

  const errorMap = useStore(form.store, (store) => store.errorMap.onSubmit);

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <div className={styles.filters}>
        <form.Field name="countryCode">
          {(field) => (
            <SelectField
              className={styles.field}
              value={field.state.value ?? null}
              label="Country"
              options={countries?.list || []}
              placeholder="Select"
              size="sm"
              isRequired
              onBlur={field.handleBlur}
              onSelectionChange={(country) => country && field.handleChange(country)}
              isLoading={countriesLoading}
              errorMessage={errorMap?.countryCode?.map((e) => e?.message).join('.')}
            />
          )}
        </form.Field>
        <form.Field name="stateCode">
          {(field) => (
            <SelectField
              className={styles.field}
              value={field.state.value as unknown as State | null}
              label="State/Province"
              options={states?.list || []}
              placeholder="Select"
              size="sm"
              onBlur={field.handleBlur}
              onSelectionChange={(state) => state && field.handleChange({ id: String(state.id), label: state.label })}
              isLoading={statesLoading}
              isDisabled={!countryCode}
            />
          )}
        </form.Field>
        <form.Field name="city">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="City"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>

        <form.Field name="portName">
          {(field) => (
            <GroupField label="Port" size="sm" className={styles.field}>
              <Input
                value={field.state.value ?? ''}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  form.setFieldValue('portKey', undefined);
                }}
                placeholder="Port name"
                size="sm"
                style={{ flex: 1 }}
              />
              <Button type="button" icon="search" ghost secondary onClick={() => setIsPortSearchOpen(true)} />
            </GroupField>
          )}
        </form.Field>
        {/* Port Postal search here */}
        {/* TODO: clarify whether this maps to the PostalFrom or PostalTo swagger param. */}
        <form.Field name="postalFrom">
          {(field) => (
            <GroupField label="Port Postal" size="sm" className={styles.field}>
              <Input
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="Port name"
                size="sm"
                style={{ flex: 1 }}
              />
              <Button type="button" icon="search" ghost secondary />
            </GroupField>
          )}
        </form.Field>
        <form.Field name="locationCode">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Location ID"
              placeholder="Location ID"
              size="sm"
            />
          )}
        </form.Field>
      </div>

      <div className={styles.actions}>
        <Button type="submit">Search</Button>
        <Button type="button" outline secondary onClick={() => formRef.current?.reset()}>
          Reset
        </Button>
      </div>

      <PortSearchModal
        isOpen={isPortSearchOpen}
        setIsOpen={setIsPortSearchOpen}
        defaultValues={{ country: countryCode }}
        onSelectionChange={(port) => {
          form.setFieldValue('portName', port.name ?? '');
          form.setFieldValue('portKey', port.portKey);
        }}
      />
    </form>
  );
};
